/**
 * React Query hooks for API calls
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cartApi, checkoutApi, ordersApi, customersApi, sessionsApi, discountApi, paystackApi, draftApi } from "./client";
import { loadLinkCart, loadSessionCart } from "./express-cart-loaders";
import type { CartSession } from "@/hooks/useCartSession";
import type { CreateOrderRequest, UpdateCartRequest, RemoveFromCartRequest, ValidateDiscountRequest, CreateDraftOrderRequest, PrefilOrderRequest } from "@/types";
import type { InitializePaystackTransactionResponse } from "./client";
import { fetchMarketplaceSession } from "@/lib/supabase-api";
import { sessionQueryKey } from "./session-cache";

/**
 * Hook to initialize a Paystack transaction on the server (for resumeTransaction flow)
 */
export function useInitializePaystackTransaction() {
  return useMutation({
    mutationFn: (metadata: any) => paystackApi.initialize(metadata),
  });
}

export type { InitializePaystackTransactionResponse };

/**
 * Query keys
 */
export const queryKeys = {
  cart: (sessionId: string) => ["cart", sessionId] as const,
  shippingOptions: () => ["shipping-options"] as const,
  order: (orderId: string) => ["order", orderId] as const,
  session: sessionQueryKey,
  marketplaceSession: (sessionId: string) => ["marketplace-session", sessionId] as const,
  linkCart: (slug: string) => ["link-cart", slug] as const,
  viewCart: (sessionId: string) => ["view-cart", sessionId] as const,
};

/**
 * Hook to fetch cart
 */
export function useCart(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId ? queryKeys.cart(sessionId) : (["cart"] as const),
    queryFn: () => cartApi.getCart(sessionId!),
    enabled: !!sessionId,
    staleTime: 0, // Always fetch fresh data
    retry: 2,
  });
}

/**
 * Hook to update cart item
 */
export function useUpdateCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateCartRequest) => cartApi.updateItem(request),
    onSuccess: (data) => {
      // Update cart in cache
      queryClient.setQueryData(queryKeys.cart(data.session_id), data);
    },
  });
}

/**
 * Hook to remove cart item
 */
export function useRemoveCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RemoveFromCartRequest) => cartApi.removeItem(request),
    onSuccess: (data) => {
      // Update cart in cache
      queryClient.setQueryData(queryKeys.cart(data.session_id), data);
    },
  });
}

/**
 * Hook to fetch shipping options
 */
export function useShippingOptions() {
  return useQuery({
    queryKey: queryKeys.shippingOptions(),
    queryFn: () => checkoutApi.getShippingOptions(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create order
 */
export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateOrderRequest) => checkoutApi.createOrder(request),
    onSuccess: () => {
      // Invalidate cart cache after order creation
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
  });
}

/**
 * Hook to pre-fill a pending order with shipping info
 */
export function usePrefilOrder() {
  return useMutation({
    mutationFn: (request: PrefilOrderRequest) => checkoutApi.prefilOrder(request),
  });
}

/**
 * Hook to fetch marketplace session data (child retailers in cart)
 */
export function useMarketplaceSession(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId ? queryKeys.marketplaceSession(sessionId) : (["marketplace-session"] as const),
    queryFn: () => fetchMarketplaceSession(sessionId!),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch order details
 */
export function useOrder(orderId: string | null) {
  return useQuery({
    queryKey: orderId ? queryKeys.order(orderId) : (["order"] as const),
    queryFn: () => ordersApi.getOrder(orderId!),
    enabled: !!orderId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch orders by session (for finding pending orders)
 */
export function useOrdersBySession(enabled: boolean) {
  return useQuery({
    queryKey: ["orders", "by-session"] as const,
    queryFn: () => ordersApi.getOrdersBySession(),
    enabled,
    staleTime: 0, // Always fetch fresh data
  });
}

export function useCustomerLookup() {
  return useMutation({
    mutationFn: ({ phoneNumber, retailerId }: { phoneNumber: string; retailerId: string }) =>
      customersApi.lookupByPhone(phoneNumber, retailerId),
  });
}

/**
 * Hook to fetch session details with attribution context
 */
export function useSession(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId ? queryKeys.session(sessionId) : (["session"] as const),
    queryFn: () => sessionsApi.getSession(sessionId!),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to validate a discount code
 */
export function useValidateDiscount() {
  return useMutation({
    mutationFn: (request: ValidateDiscountRequest) => discountApi.validate(request),
  });
}

/**
 * Hook to create a draft order
 */
export function useCreateDraftOrder() {
  return useMutation({
    mutationFn: ({ request, method }: { request: CreateDraftOrderRequest; method?: "POST" | "PUT" }) =>
      draftApi.DraftOrder(request, method ?? "POST"),
  });
}

/**
 * Express cart pages. Both load once and are then edited by the page itself,
 * so they never refetch on their own (that would reset the shopper's lines).
 */
export function useLinkCart(slug: string | null) {
  return useQuery({
    queryKey: slug ? queryKeys.linkCart(slug) : (["link-cart"] as const),
    queryFn: () => loadLinkCart(slug!),
    enabled: !!slug,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useViewCart(session: CartSession | null) {
  return useQuery({
    queryKey: session ? queryKeys.viewCart(session.session_id) : (["view-cart"] as const),
    queryFn: () => loadSessionCart(session!),
    enabled: !!session,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}
